import React from 'react'
import { OmDropdownMenu, type OmMenuItem } from '@owma/kit'
import chevronDownIcon from 'assets/images/chevron-down-24.svg'
import checkIcon from 'assets/images/menu-check.svg'
import classNames from 'classnames'
import { useRef, CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import { basicColorNames, basicColors } from 'utils/ui'
import styles from './style.module.scss'

interface ColorButtonProps {
  color: string
  showName?: boolean
  onChange: (color: string) => void
  forceHeight?: string | null
}

const ColorButton = ({
  color,
  showName = false,
  onChange,
  forceHeight = null,
}: ColorButtonProps) => {
  const { t } = useTranslation()
  const selectedItemRef = useRef<HTMLDivElement>(null)

  const getColorItems = () => {
    const currentColor = color
    const items: OmMenuItem[] = basicColors.map((color, index) => {
      const isSelected = currentColor === color
      return {
        key: color,
        label: (
          <div
            className={styles.menuItem}
            ref={isSelected ? selectedItemRef : null}
            id={isSelected ? 'selected-color-item' : undefined}
          >
            <div className={styles.menuItemContent}>
              <div className={styles.colorDot} style={{ backgroundColor: color }} />
              <div className={styles.colorName}>{basicColorNames()[index]}</div>
            </div>
            {isSelected && <img src={checkIcon} alt="Selected" className={styles.checkIcon} />}
          </div>
        ),
        onClick: () => onChange(color),
      }
    })

    // 현재 색상이 기본 색상 목록에 없는 경우 Custom 항목 추가
    const customColor = currentColor
    if (!basicColors.map((color) => color.includes(customColor))) {
      items.push({
        type: 'divider' as const,
        key: 'divider',
        label: null,
        onClick: () => {},
      })
      items.push({
        key: 'custom',
        label: (
          <div className={styles.menuItem} ref={selectedItemRef} id="selected-color-item">
            <div className={styles.menuItemContent}>
              <div className={styles.colorDot} style={{ backgroundColor: customColor }} />
              <div className={styles.colorName}>{t('calendar.recurrence.customRecurrence')}</div>
            </div>
            <img src={checkIcon} alt="Selected" className={styles.checkIcon} />
          </div>
        ),
        onClick: () => {},
      })
    }

    return items
  }

  const getColorName = (): string => {
    const index = basicColors.findIndex((c) => c === color)
    if (index === -1) {
      return t('calendar.recurrence.customRecurrence')
    }
    return basicColorNames()[index]
  }

  // 드롭다운이 열릴 때 선택된 아이템으로 스크롤
  const onDropdownVisibleChange = (visible: boolean): void => {
    if (visible) {
      setTimeout(() => {
        const selectedItem = document.getElementById('selected-color-item')
        if (selectedItem) {
          selectedItem.scrollIntoView({ block: 'center', behavior: 'auto' })
        }
      }, 100)
    }
  }

  const getColorButtonStyle = (): CSSProperties => {
    const style: CSSProperties & { '--width'?: string } = showName ? { '--width': '100%' } : {}
    if (forceHeight) {
      style.height = forceHeight
    }
    return style
  }

  return (
    <OmDropdownMenu
      menu={{ items: getColorItems() }}
      trigger={['click']}
      placement="bottom"
      getPopupContainer={() => document.body}
      overlayClassName={classNames('contextMenu', styles.dropdown)}
      overlayStyle={{ maxHeight: '350px', overflow: 'auto' }}
      align={{ offset: [showName ? -32 : -25, 0] }}
      onOpenChange={onDropdownVisibleChange}
    >
      <div className={styles.colorButton} style={getColorButtonStyle()}>
        <div className={styles.colorButtonContent}>
          <div className={styles.colorDot} style={{ backgroundColor: color }} />
          {showName && <div className={styles.colorName}>{getColorName()}</div>}
        </div>
        <img src={chevronDownIcon} alt="More" />
      </div>
    </OmDropdownMenu>
  )
}

export default ColorButton
